/**
 * Everything the shelf needs to fetch, and the URL it keeps itself in.
 *
 * Three things drive the shape of this file:
 *
 * 1. The filter state is the URL, not a copy of it. A shelf that cannot be
 *    bookmarked or sent to someone else is a shelf that has to be rebuilt by
 *    hand every time, so the hash query is the single source of truth and the
 *    hooks below only ever read it.
 * 2. Paging is append-only and the requests race. A fast scroll fires page 3
 *    before page 2 lands, and a filter change fires page 0 while both are still
 *    in flight; every response therefore carries the id of the run that asked
 *    for it, and a response from an older run is dropped rather than merged.
 * 3. Aborting is not an optimisation. Without it a filter typed quickly leaves a
 *    dozen open sockets and the last one to land wins, which is not the last one
 *    the user asked for.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError, api } from "../lib/api";
import { offlineShelf } from "../lib/offline";
import { onReadingLangChange, readingLang, rememberHouseLang } from "../lib/lang";
import { t } from "../i18n";
import type { BookSummary, Facets, LibraryQuery, ReadingStatus } from "../lib/types";

/** How many books one page of the grid asks for. */
export const PAGE_SIZE = 48;

/** How many books the Continue row shows at most. Past this nobody scrolls. */
const CONTINUE_LIMIT = 12;

export type SortKey = "author" | "title" | "recent" | "progress";

export interface Filters {
  q: string;
  lang: string;
  shelf: string;
  genre: string;
  status: ReadingStatus | "";
  sort: SortKey;
}

export const EMPTY_FILTERS: Filters = {
  q: "",
  lang: "",
  shelf: "",
  genre: "",
  status: "",
  sort: "author",
};

const STATUSES: ReadingStatus[] = ["reading", "finished", "unread"];
const SORTS: SortKey[] = ["author", "title", "recent", "progress"];

function isStatus(value: string): value is ReadingStatus {
  return (STATUSES as string[]).includes(value);
}

function isSort(value: string): value is SortKey {
  return (SORTS as string[]).includes(value);
}

/**
 * The library lives at `#/` and carries its filters as a query on the hash:
 * `#/?shelf=Musique&status=reading`. The hash is used rather than the real query
 * string because the whole app is hash routed and a static host must be able to
 * serve one file for every URL.
 */
export function readFilters(hash: string): Filters {
  const mark = hash.indexOf("?");
  const params = new URLSearchParams(mark >= 0 ? hash.slice(mark + 1) : "");
  const status = params.get("status") ?? "";
  const sort = params.get("sort") ?? "";
  return {
    q: params.get("q") ?? "",
    lang: params.get("lang") ?? "",
    shelf: params.get("shelf") ?? "",
    genre: params.get("genre") ?? "",
    status: isStatus(status) ? status : "",
    sort: isSort(sort) ? sort : "author",
  };
}

export function filtersToHash(filters: Filters): string {
  const params = new URLSearchParams();
  if (filters.q) params.set("q", filters.q);
  if (filters.lang) params.set("lang", filters.lang);
  if (filters.shelf) params.set("shelf", filters.shelf);
  if (filters.genre) params.set("genre", filters.genre);
  if (filters.status) params.set("status", filters.status);
  // `author` is the default the API applies anyway, so leaving it out keeps
  // shared URLs short and readable.
  if (filters.sort !== "author") params.set("sort", filters.sort);
  const encoded = params.toString();
  return encoded ? `#/?${encoded}` : "#/";
}

export function countActive(filters: Filters): number {
  return [filters.q, filters.lang, filters.shelf, filters.genre, filters.status].filter(Boolean)
    .length;
}

/** The filters as the URL currently holds them, kept in step with the address bar. */
export function useFilters(): {
  filters: Filters;
  setFilters: (next: Filters) => void;
  patch: (part: Partial<Filters>) => void;
  reset: () => void;
} {
  const [filters, setState] = useState<Filters>(() => readFilters(location.hash));

  useEffect(() => {
    const update = () => setState(readFilters(location.hash));
    window.addEventListener("hashchange", update);
    return () => window.removeEventListener("hashchange", update);
  }, []);

  const setFilters = useCallback((next: Filters) => {
    const target = filtersToHash(next);
    // Writing the hash fires `hashchange`, which is what actually updates the
    // state. Setting it here as well would render twice for one change.
    if (location.hash !== target) location.hash = target;
    else setState(next);
  }, []);

  const patch = useCallback(
    (part: Partial<Filters>) => setFilters({ ...readFilters(location.hash), ...part }),
    [setFilters],
  );

  const reset = useCallback(() => setFilters(EMPTY_FILTERS), [setFilters]);

  return { filters, setFilters, patch, reset };
}

function toQuery(filters: Filters, prefer: string, offset: number): LibraryQuery {
  return {
    q: filters.q || undefined,
    lang: filters.lang || undefined,
    shelf: filters.shelf || undefined,
    genre: filters.genre || undefined,
    status: filters.status || undefined,
    sort: filters.sort,
    // A work, not an edition: one card for a book that exists in two languages.
    // The language chips still filter, and filtering happens first, so picking
    // English does not leave you looking at French cards.
    prefer,
    limit: PAGE_SIZE,
    offset,
  };
}

/** The reading language, kept in step with the preference and with other tabs. */
export function useReadingLang(): string {
  const [lang, setLang] = useState(readingLang);
  useEffect(() => onReadingLangChange(() => setLang(readingLang())), []);
  return lang;
}

export interface LibraryState {
  books: BookSummary[];
  total: number;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  /** True when the current page came from the offline cache rather than the
      server, whether or not that cache actually held any book. The screen
      uses it to explain the situation instead of showing a raw fetch error
      or a blank grid. */
  offline: boolean;
  hasMore: boolean;
  loadMore: () => void;
}

/** The grid: one page at a time, appended, with the races handled. */
export function useLibraryBooks(filters: Filters): LibraryState {
  const [books, setBooks] = useState<BookSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);

  // A monotonic id for the current set of filters. Anything stamped with an
  // older one is a reply to a question nobody is asking any more.
  const run = useRef(0);
  const offset = useRef(0);
  const inFlight = useRef<AbortController | null>(null);
  const exhausted = useRef(false);

  const prefer = useReadingLang();

  const fetchPage = useCallback(
    (target: Filters, at: number, id: number) => {
      inFlight.current?.abort();
      const controller = new AbortController();
      inFlight.current = controller;
      if (at === 0) setLoading(true);
      else setLoadingMore(true);

      api
        .library(toQuery(target, prefer, at), controller.signal)
        .then((page) => {
          if (run.current !== id) return;
          setError(null);
          setOffline(false);
          // The server has just resolved `auto` against rows this browser will
          // never see. Keeping the answer is what lets the interface open in the
          // library's language on the next visit, and on the sign-in screen,
          // where no library is ever loaded.
          rememberHouseLang(page.lang);
          setTotal(page.total);
          exhausted.current = page.books.length < PAGE_SIZE;
          offset.current = at + page.books.length;
          setBooks((current) => (at === 0 ? page.books : [...current, ...page.books]));
        })
        .catch(async (cause: unknown) => {
          if (controller.signal.aborted || run.current !== id) return;
          // An ApiError means the server answered and refused. Anything else
          // means nothing answered, and a reader on a train is owed the books
          // they downloaded for exactly this moment rather than an error, be
          // there one such book or none: either way the screen shows the
          // offline shelf, not a raw fetch failure.
          if (!(cause instanceof ApiError) && at === 0) {
            const shelf = await offlineShelf();
            if (run.current !== id) return;
            setError(null);
            setOffline(true);
            setBooks(shelf);
            setTotal(shelf.length);
            exhausted.current = true;
            return;
          }
          setOffline(false);
          setError(cause instanceof Error ? cause.message : t("libLoadFailed"));
          if (at === 0) setBooks([]);
          exhausted.current = true;
        })
        .finally(() => {
          if (run.current !== id) return;
          setLoading(false);
          setLoadingMore(false);
        });
    },
    [prefer],
  );

  // One dependency, flattened: an object literal would be a new value on every
  // render and would refetch the whole shelf sixty times a second. The reading
  // language belongs in it: crossing to another edition inside a book changes
  // which edition every card in the grid stands for.
  const key = `${filters.q}|${filters.lang}|${filters.shelf}|${filters.genre}|${filters.status}|${filters.sort}|${prefer}`;

  useEffect(() => {
    const id = ++run.current;
    offset.current = 0;
    exhausted.current = false;
    setBooks([]);
    setTotal(0);
    fetchPage(filters, 0, id);
    return () => inFlight.current?.abort();
    // `key` stands in for `filters`: it is the same six fields, flattened.
  }, [key, filters, fetchPage]);

  const hasMore = !exhausted.current && books.length < total;

  const loadMore = useCallback(() => {
    if (loading || loadingMore || exhausted.current) return;
    fetchPage(filters, offset.current, run.current);
  }, [filters, loading, loadingMore, fetchPage]);

  return { books, total, loading, loadingMore, error, offline, hasMore, loadMore };
}

/** The books with a position, freshest first. The one row everybody uses. */
export function useContinue(): { books: BookSummary[]; loading: boolean } {
  const [books, setBooks] = useState<BookSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    api
      // No `prefer` here, deliberately. This row is a list of positions, not of
      // works: collapsing editions would show you the French cover of a book you
      // are three hours into in English, and take you somewhere you have never
      // been when you tapped it.
      .library(
        { status: "reading", sort: "recent", limit: CONTINUE_LIMIT },
        controller.signal,
      )
      .then((page) => setBooks(page.books))
      .catch(() => setBooks([]))
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  return { books, loading };
}

export function useFacets(): Facets | null {
  const [facets, setFacets] = useState<Facets | null>(null);
  // The same language the grid is asking for. Without it the status counts
  // would be counted over editions while the cards beside them are works, and
  // the two numbers would disagree on screen.
  const prefer = useReadingLang();
  useEffect(() => {
    const controller = new AbortController();
    api
      .facets(prefer, controller.signal)
      .then(setFacets)
      .catch(() => setFacets(null));
    return () => controller.abort();
  }, [prefer]);
  return facets;
}

/**
 * How the shelf is laid out: covers, or one line a book.
 *
 * A preference of the device, not of the library, so it lives in local storage
 * rather than in the URL: a phone wants the list and the desk wants the covers,
 * and a link shared between them should not carry the sender's screen with it.
 */
export type ViewMode = "grid" | "list";

const VIEW_KEY = "readcommons:library-view";

export function readViewMode(): ViewMode {
  try {
    return localStorage.getItem(VIEW_KEY) === "list" ? "list" : "grid";
  } catch {
    // Private browsing denies storage. Covers are the better default anyway.
    return "grid";
  }
}

export function useViewMode(): { view: ViewMode; setView: (next: ViewMode) => void } {
  const [view, setState] = useState<ViewMode>(readViewMode);
  const setView = useCallback((next: ViewMode) => {
    setState(next);
    try {
      localStorage.setItem(VIEW_KEY, next);
    } catch {
      // Not remembering it is survivable; applying it now is not.
    }
  }, []);
  return { view, setView };
}

/** The count a status chip carries, or null when the server has not said. */
export function statusCount(facets: Facets | null, status: ReadingStatus | ""): number | null {
  if (!facets?.statuses?.length) return null;
  if (status === "") {
    return facets.statuses.reduce((sum, facet) => sum + facet.count, 0);
  }
  return facets.statuses.find((facet) => facet.name === status)?.count ?? 0;
}

/**
 * Calls `onHit` when the sentinel scrolls into view. Kept here so the grid only
 * has to render a div and hand back its ref.
 */
export function useSentinel(enabled: boolean, onHit: () => void): (node: HTMLElement | null) => void {
  const callback = useRef(onHit);
  callback.current = onHit;
  const [node, setNode] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!node || !enabled) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) callback.current();
      },
      // Ask early: a page that only starts loading when the sentinel is on
      // screen shows a hole at the bottom of every scroll.
      { rootMargin: "800px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, enabled]);

  return setNode;
}

/**
 * Debounced value, used by the search overlay. 200 ms is long enough that a
 * typed word is one request and short enough that the panel never feels stuck.
 */
export function useDebounced<T>(value: T, delay = 200): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setSettled(value), delay);
    return () => window.clearTimeout(timer);
  }, [value, delay]);
  return settled;
}

/** Books per shelf, so the genre chips can narrow to the selected shelf. */
export function genresForShelf(facets: Facets | null, shelf: string) {
  const genres = facets?.genres ?? [];
  if (!shelf) return genres;
  const narrowed = genres.filter((genre) => genre.shelf === shelf);
  // A library that never filled in shelves on its genres would otherwise show an
  // empty genre row the moment a shelf is picked, which reads as a bug.
  return narrowed.length > 0 ? narrowed : [];
}
